import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  clearChallenge,
  deleteProxyConfig,
  getExecutionAccountByUser,
  putProxyConfig,
  setExecutionMode,
  testProxy,
  type ExecutionAccount,
  type ProxyTestResult,
} from "@/services/executionAccountApi";

/**
 * Panneau « compte d'exécution » de la page détail d'un abonnement.
 *
 * Deux choses s'y règlent, et une seule des deux est réversible sans risque :
 * le proxy de sortie (éditable librement) et le mode d'exécution (passer en
 * `server` sans proxy valide est refusé côté API — un compte serveur sans
 * proxy sort par l'IP du cluster et corrèle toute la flotte).
 *
 * Le mot de passe n'est jamais reçu du serveur. Le champ est donc vide par
 * défaut et signalé « inchangé » : le laisser vide conserve celui qui est
 * stocké.
 */

type Form = {
  provider: string;
  endpoint: string;
  username: string;
  secret: string;
  sticky_session_id: string;
  country: string;
  city: string;
  timezone: string;
  locale: string;
};

const EMPTY: Form = {
  provider: "",
  endpoint: "",
  username: "",
  secret: "",
  sticky_session_id: "",
  country: "",
  city: "",
  timezone: "",
  locale: "",
};

function fromAccount(acc: ExecutionAccount | null): Form {
  const isp = acc?.proxy?.isp_proxy;
  const geo = acc?.proxy?.geo;
  return {
    provider: isp?.provider ?? "",
    endpoint: isp?.endpoint ?? "",
    username: isp?.username ?? "",
    secret: "", // jamais renvoyé par l'API
    sticky_session_id: isp?.sticky_session_id ?? "",
    country: geo?.country ?? "",
    city: geo?.city ?? "",
    timezone: geo?.timezone ?? "",
    locale: geo?.locale ?? "",
  };
}

function TestVerdict({ result }: { result: ProxyTestResult }) {
  if (result.error) {
    return <span className="text-red-600">Proxy injoignable : {result.error}</span>;
  }
  const problems: string[] = [];
  // `hosting` is only set by a probe that types the address; with the default
  // HTTPS probe the operator reads the ASN line below (a datacenter ASN is the
  // signal).
  if (result.hosting) problems.push("IP typée datacenter (hosting)");
  if (result.timezone_match === false) {
    problems.push(`fuseau de sortie ${result.seen_timezone} différent du fuseau déclaré`);
  }
  if (result.country_match === false) {
    problems.push(
      `sort en ${result.seen_country} alors que ${result.declared_country} est déclaré`,
    );
  }
  return (
    <div className="space-y-1">
      <div className={result.ok ? "text-green-700" : "text-red-600"}>
        {result.ok ? "Sortie utilisable" : "Sortie à ne pas utiliser"} — {result.seen_ip ?? "?"}{" "}
        {result.seen_country ? `(${result.seen_country})` : ""}
      </div>
      <div className="text-xs text-gray-600">
        {result.asn ?? "ASN inconnu"}
        {result.city ? ` · ${result.city}` : ""}
      </div>
      {problems.length > 0 && (
        <ul className="list-inside list-disc text-xs text-red-600">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ExecutionAccountPanel({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [account, setAccount] = useState<ExecutionAccount | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<ProxyTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const acc = await getExecutionAccountByUser(userId, navigate);
      setAccount(acc);
      setForm(fromAccount(acc));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save() {
    if (!account) return;
    setBusy(true);
    try {
      await putProxyConfig(
        account.subscription_id,
        {
          isp_proxy: {
            provider: form.provider || null,
            endpoint: form.endpoint,
            username: form.username || null,
            // Champ vide = « inchangé ». On ne l'envoie pas du tout, sinon
            // l'API le lirait comme un effacement explicite.
            ...(form.secret ? { password: form.secret } : {}),
            sticky_session_id: form.sticky_session_id || null,
          },
          geo: {
            country: form.country || null,
            city: form.city || null,
            timezone: form.timezone || null,
            locale: form.locale || null,
          },
        },
        navigate,
      );
      toast({ title: "Proxy enregistré" });
      setTestResult(null);
      await reload();
    } catch (e) {
      toast({
        title: "Enregistrement refusé",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  async function runTest() {
    if (!account) return;
    setBusy(true);
    try {
      setTestResult(await testProxy(account.subscription_id, navigate));
    } catch (e) {
      toast({
        title: "Test impossible",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  async function toggleMode() {
    if (!account) return;
    const next = account.execution_mode === "server" ? "plugin" : "server";
    setBusy(true);
    try {
      await setExecutionMode(account.subscription_id, next, navigate);
      toast({ title: `Mode d'exécution : ${next}` });
      await reload();
    } catch (e) {
      toast({
        title: "Changement refusé",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  async function liftQuarantine() {
    if (!account) return;
    setBusy(true);
    try {
      await clearChallenge(account.subscription_id, navigate);
      toast({ title: "Quarantaine levée" });
      await reload();
    } catch (e) {
      toast({
        title: "Levée refusée",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  async function clearProxy() {
    if (!account) return;
    setBusy(true);
    try {
      await deleteProxyConfig(account.subscription_id, navigate);
      toast({ title: "Proxy retiré" });
      setTestResult(null);
      await reload();
    } catch (e) {
      toast({
        title: "Suppression refusée",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="text-sm text-gray-500">Chargement du compte d'exécution...</div>;
  }
  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!account) return null;

  const isServer = account.execution_mode === "server";
  const hasSecret = account.proxy?.isp_proxy?.password_set ?? false;

  return (
    <div className="space-y-4 rounded border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${
            isServer ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"
          }`}
        >
          {isServer
            ? "Exécution serveur (browserfarm)"
            : "Exécution plugin (navigateur du recruteur)"}
        </span>
        <Button size="sm" variant="outline" disabled={busy} onClick={toggleMode}>
          {isServer ? "Repasser en plugin" : "Passer en serveur"}
        </Button>
        {account.challenged_at && (
          <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
            Challenge LinkedIn {account.challenge_kind ?? ""} le{" "}
            {new Date(account.challenged_at).toLocaleDateString("fr-FR")} — compte en quarantaine
          </span>
        )}
        {account.challenged_at && (
          <Button size="sm" variant="outline" disabled={busy} onClick={liftQuarantine}>
            Lever la quarantaine (compte vérifié)
          </Button>
        )}
      </div>

      <p className="text-xs text-gray-600">
        En mode plugin, le compte sort par l&apos;IP du recruteur : le proxy ci-dessous n&apos;est
        pas utilisé. En mode serveur il l&apos;est pour chaque action, et passer en serveur sans
        proxy valide est refusé.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-3">
          <h4 className="text-sm font-semibold">Proxy de sortie</h4>
          <div>
            <Label htmlFor="ea-endpoint">Endpoint</Label>
            <Input
              id="ea-endpoint"
              value={form.endpoint}
              onChange={set("endpoint")}
              placeholder="isp.decodo.com:10002"
            />
          </div>
          <div>
            <Label htmlFor="ea-provider">Fournisseur</Label>
            <Input
              id="ea-provider"
              value={form.provider}
              onChange={set("provider")}
              placeholder="decodo"
            />
          </div>
          <div>
            <Label htmlFor="ea-username">Utilisateur</Label>
            <Input id="ea-username" value={form.username} onChange={set("username")} />
          </div>
          <div>
            <Label htmlFor="ea-secret">
              Mot de passe{" "}
              <span className="text-xs font-normal text-gray-500">
                {hasSecret ? "(configuré — laisser vide pour ne pas le changer)" : "(aucun)"}
              </span>
            </Label>
            <Input
              id="ea-secret"
              type="password"
              value={form.secret}
              onChange={set("secret")}
              autoComplete="new-password"
              placeholder={hasSecret ? "inchangé" : ""}
            />
          </div>
          <div>
            <Label htmlFor="ea-sticky">Session sticky</Label>
            <Input
              id="ea-sticky"
              value={form.sticky_session_id}
              onChange={set("sticky_session_id")}
              placeholder={`sub-${account.subscription_id}`}
            />
            <p className="mt-1 text-xs text-gray-500">
              Sans elle l&apos;IP de sortie tourne à chaque requête, et le compte voit sa propre
              adresse changer entre deux actions.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-semibold">Géo annoncée</h4>
          <p className="text-xs text-gray-500">
            Le navigateur du pod prend sa locale et son fuseau d&apos;ici. Ils doivent correspondre
            au pays réel de sortie du proxy — une IP française annonçant en-US / America/New_York
            est un signal plus fort que pas de proxy du tout.
          </p>
          <div>
            <Label htmlFor="ea-country">Pays (ISO 3166-1 alpha-2)</Label>
            <Input
              id="ea-country"
              value={form.country}
              onChange={set("country")}
              maxLength={2}
              placeholder="FR"
            />
          </div>
          <div>
            <Label htmlFor="ea-city">Ville</Label>
            <Input id="ea-city" value={form.city} onChange={set("city")} placeholder="Paris" />
          </div>
          <div>
            <Label htmlFor="ea-timezone">Fuseau</Label>
            <Input
              id="ea-timezone"
              value={form.timezone}
              onChange={set("timezone")}
              placeholder="Europe/Paris"
            />
          </div>
          <div>
            <Label htmlFor="ea-locale">Locale</Label>
            <Input
              id="ea-locale"
              value={form.locale}
              onChange={set("locale")}
              placeholder="fr_FR"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy || !form.endpoint} onClick={save}>
          Enregistrer
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !account.proxy}
          onClick={runTest}
          title="Interroge un service de réputation IP à travers ce proxy"
        >
          Tester le proxy
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy || !account.proxy || isServer}
          onClick={clearProxy}
        >
          Retirer
        </Button>
      </div>

      {testResult && (
        <div className="rounded border bg-gray-50 p-3 text-sm">
          <TestVerdict result={testResult} />
        </div>
      )}
    </div>
  );
}
